import React, { useState, ReactNode } from 'react'
import './index.scss'
import clsx from 'clsx'

interface UseFancyNumberParams {
	initialNumber: number
	minNumber: number
	maxNumber: number
	step: number
}

function roundToStep(value: number, step: number) {
	return Math.round(value / step) * step
}

export function deriveFancyStep(minNumber: number, maxNumber: number) {
	const gap = maxNumber - minNumber

	if (gap < 100) {
		return 10
	} else if (gap < 1000) {
		return 50
	} else if (gap < 10000) {
		return 100
	} else {
		return 1000
	}
}

export function useFancyNumber({
	initialNumber,
	minNumber,
	maxNumber,
	step,
}: UseFancyNumberParams) {
	const [number, setNumber] = useState(initialNumber)
	const offSetFromStep = number % step

	function onIncrease() {
		const calculatedStep = offSetFromStep ? step - offSetFromStep : step
		setNumber((prev) => {
			return prev + calculatedStep < maxNumber
				? prev + calculatedStep
				: maxNumber
		})
	}
	function onDecrease() {
		const calculatedStep = offSetFromStep || step
		setNumber((prev) => {
			return prev - calculatedStep > minNumber
				? prev - calculatedStep
				: minNumber
		})
	}

	function onValidate() {
		if (number > maxNumber) {
			setNumber(maxNumber)
		} else if (number < minNumber) {
			setNumber(minNumber)
		} else {
			setNumber(number)
		}
	}

	const percentage = ((number - minNumber) / (maxNumber - minNumber)) * 100

	return {
		initialNumber,
		minNumber,
		maxNumber,
		step,
		onIncrease,
		onDecrease,
		number,
		onNumberChange: setNumber,
		onValidate,
		percentage,
		setNumber,
	}
}

interface FancyNumberProps {
	children: React.ReactNode
}

export function FancyNumber({ children }: FancyNumberProps) {
	return <div className="fancy-number">{children}</div>
}

interface FancyNumberInputProps {
	onValidate: () => void
	onDecrease: () => void
	onIncrease: () => void
	number: number
	initialNumber: number
	onNumberChange: React.Dispatch<React.SetStateAction<number>>
	disabled?: boolean
}

export function FancyNumberInput({
	onValidate,
	onDecrease,
	onIncrease,
	number,
	initialNumber,
	onNumberChange,
	disabled,
}: FancyNumberInputProps) {
	const [isMinusSpinning, setMinusSpinning] = useState(false)
	const [isPlusSpinning, setPlusSpinning] = useState(false)

	return (
		<div className="number-field-container">
			<button
				onClick={() => {
					setMinusSpinning(true)
					onDecrease()
				}}
				className={clsx('step-button minus', isMinusSpinning && 'spin')}
				onTransitionEnd={() => setMinusSpinning(false)}
				disabled={disabled}
			/>
			<input
				value={number}
				onChange={(e) => onNumberChange(Number(e.target.value))}
				onBlur={onValidate}
				type="number"
				className="number-field"
				disabled={disabled}
				onFocus={(e) => {
					if (initialNumber === number) {
						e.target.select()
					}
				}}
			/>
			<button
				onClick={() => {
					setPlusSpinning(true)
					onIncrease()
				}}
				className={clsx('step-button plus', isPlusSpinning && 'spin')}
				onTransitionEnd={() => setPlusSpinning(false)}
				disabled={disabled}
			/>
		</div>
	)
}

interface FancySliderProps {
	initialNumber: number
	minNumber: number
	maxNumber: number
	step: number
	onIncrease: () => void
	onDecrease: () => void
	number: number
	onNumberChange: React.Dispatch<React.SetStateAction<number>>
	onValidate: () => void
	percentage: number
	disabled?: boolean
}

export function FancyNumberSlider({
	step,
	maxNumber,
	minNumber,
	onNumberChange,
	number,
	percentage,
	disabled,
}: FancySliderProps) {
	function handleChange(value: number) {
		const roundedValue = roundToStep(value, step)
		const isNotReachableLowerLimit = minNumber > roundedValue
		const isNotReachableUpperLimit = maxNumber - roundedValue < step

		// We're a bit too smart here beacuse we need to. If number is not reachable beacuse of step value we handle it. Otherwise we round it.
		if (isNotReachableLowerLimit) {
			onNumberChange(minNumber)
		} else if (isNotReachableUpperLimit) {
			onNumberChange(maxNumber)
		} else {
			onNumberChange(roundedValue)
		}
	}

	return (
		<span
			className={clsx(
				'range-parent',
				disabled && 'range-parent--disabled',
			)}
			style={{
				backgroundImage: `linear-gradient(to right, rgb(222, 153, 111) ${percentage}%, rgb(248, 199, 169) ${percentage}%)`,
			}}>
			<input
				type="range"
				min={minNumber}
				max={maxNumber}
				step={1}
				value={number}
				onChange={({ target }) => handleChange(Number(target.value))}
				className="slider-control"
				disabled={disabled}
			/>
		</span>
	)
}

function FancyNumberInputLabel({ children }: { children: ReactNode }) {
	return <span className="fancy-number-label">{children}</span>
}

function FancyNumberSliderLabel({
	left,
	right,
}: {
	left: ReactNode
	right: ReactNode
}) {
	return (
		<div className="fancy-number-descprition">
			<span>{left}</span>
			<span>{right}</span>
		</div>
	)
}

FancyNumber.Input = FancyNumberInput
FancyNumber.InputLabel = FancyNumberInputLabel
FancyNumber.Slider = FancyNumberSlider
FancyNumber.SliderLabel = FancyNumberSliderLabel
